import {
  forwardRef,
  useLayoutEffect,
  useRef,
  type InputHTMLAttributes,
} from 'react';

type MoneyAmountInputProps = InputHTMLAttributes<HTMLInputElement> & {
  valueForWidth?: string;
};

export const MoneyAmountInput = forwardRef<
  HTMLInputElement,
  MoneyAmountInputProps
>(function MoneyAmountInput(
  { valueForWidth = '', placeholder = '0', ...inputProps },
  ref,
) {
  const measuredValue = valueForWidth.length > 0
    ? valueForWidth
    : String(placeholder);
  const rowRef = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const row = rowRef.current;
    const mirror = row?.querySelector<HTMLElement>('.money-amount-input > span');
    const input = row?.querySelector('input');
    if (!row || !mirror || !input) return;
    let active = true;
    const resize = () => {
      if (!active) return;
      const range = document.createRange();
      range.selectNodeContents(mirror);
      const naturalWidth = range.getBoundingClientRect().width;
      const availableWidth = input.clientWidth - 2;
      const fontSize = Number.parseFloat(getComputedStyle(mirror).fontSize);
      if (availableWidth > 0 && naturalWidth > 0) {
        // The mirror always measures the native 50px text. Only the editable
        // glyphs shrink, leaving the row, currency and stored amount intact.
        input.style.fontSize = `${Math.min(fontSize, fontSize * availableWidth / naturalWidth)}px`;
      }
    };
    resize();
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(resize);
    observer?.observe(row);
    void document.fonts?.ready.then(resize);
    return () => { active = false; observer?.disconnect(); };
  }, [measuredValue]);

  return (
    <span ref={rowRef} className="money-amount-row">
      <span className="money-amount-input">
        <span aria-hidden>{measuredValue}</span>
        <input
          {...inputProps}
          ref={ref}
          placeholder={placeholder}
        />
      </span>
      <b className="money-amount-currency" aria-hidden>₽</b>
    </span>
  );
});
